import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, Image, Modal, TextInput, TouchableOpacity, Platform } from 'react-native';
import { Text, Card, Button, Divider, ActivityIndicator, DataTable } from 'react-native-paper';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import { useAuth } from '../../contexts/AuthContext';
import productosApi from '../../services/productosApi';
import mainApi from '../../services/api';
const api = productosApi;

const formatDateYMD = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const ProductoDetalleScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { user } = useAuth();
  const { productoId } = route.params || {};
  const [producto, setProducto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lotes, setLotes] = useState([]);
  const [loadingLotes, setLoadingLotes] = useState(false);
  const [showLoteModal, setShowLoteModal] = useState(false);
  const [savingLote, setSavingLote] = useState(false);
  const [loteForm, setLoteForm] = useState({
    numero_lote: '',
    fecha_vencimiento: '',
    cantidad_inicial: '',
    fecha_fabricacion: '',
    costo_unitario: '',
  });
  const [showDatePickerVenc, setShowDatePickerVenc] = useState(false);
  const [showDatePickerFab, setShowDatePickerFab] = useState(false);
  const [fechaVencDate, setFechaVencDate] = useState(null);
  const [fechaFabDate, setFechaFabDate] = useState(null);

  const loadProductoDetalle = useCallback(async () => {
    try {
      setLoading(true);
      // Cargar datos desde la API
      const productoData = await api.getProducto(productoId);
      console.log('Detalle de producto cargado:', productoData);
      setProducto(productoData);
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar detalle del producto:', error);
      setLoading(false);
      Alert.alert('Error', 'No se pudo cargar la información del producto');
    }
  }, [productoId]);

  const loadLotes = useCallback(async () => {
    try {
      setLoadingLotes(true);
      const lotesData = await mainApi.getLotesProducto(productoId);
      setLotes(Array.isArray(lotesData) ? lotesData : []);
    } catch (error) {
      console.error('Error al cargar lotes:', error);
      setLotes([]);
    } finally {
      setLoadingLotes(false);
    }
  }, [productoId]);

  useEffect(() => {
    loadProductoDetalle();
    loadLotes();
  }, [loadProductoDetalle, loadLotes]);

  // Recargar datos cuando la pantalla recibe el foco (ej. al volver de editar)
  useFocusEffect(
    useCallback(() => {
      loadProductoDetalle();
      loadLotes();
    }, [loadProductoDetalle, loadLotes])
  );

  const handleEdit = () => {
    navigation.navigate('EditarProducto', { producto });
  };

  const handleDelete = () => {
    Alert.alert(
      'Confirmar eliminación',
      `¿Está seguro que desea eliminar el producto "${producto?.nombre}" del inventario?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Eliminar',
          style: 'destructive',
          onPress: async () => {
            try {
              setLoading(true);
              await api.deleteProducto(productoId);
              setLoading(false);
              Alert.alert('Éxito', 'Producto eliminado correctamente');
              navigation.goBack();
            } catch (error) {
              setLoading(false);
              console.error('Error al eliminar producto:', error);
              Alert.alert('Error', 'No se pudo eliminar el producto: ' + (error.message || 'Error desconocido'));
            }
          },
        },
      ]
    );
  };

  const handleGuardarLote = async () => {
    if (!loteForm.numero_lote.trim()) {
      Alert.alert('Error', 'El número de lote es requerido');
      return;
    }
    if (!loteForm.fecha_vencimiento.trim()) {
      Alert.alert('Error', 'La fecha de vencimiento es requerida');
      return;
    }
    if (!loteForm.cantidad_inicial.trim()) {
      Alert.alert('Error', 'La cantidad inicial es requerida');
      return;
    }
    try {
      setSavingLote(true);
      await mainApi.createLote({
        producto_id: productoId,
        numero_lote: loteForm.numero_lote.trim(),
        fecha_vencimiento: loteForm.fecha_vencimiento.trim(),
        cantidad_inicial: parseFloat(loteForm.cantidad_inicial),
        fecha_fabricacion: loteForm.fecha_fabricacion.trim() || undefined,
        costo_unitario: loteForm.costo_unitario.trim() ? parseFloat(loteForm.costo_unitario) : undefined,
      });
      setShowLoteModal(false);
      setLoteForm({ numero_lote: '', fecha_vencimiento: '', cantidad_inicial: '', fecha_fabricacion: '', costo_unitario: '' });
      setFechaVencDate(null);
      setFechaFabDate(null);
      await loadLotes();
      Alert.alert('Éxito', 'Lote agregado correctamente');
    } catch (error) {
      console.error('Error al crear lote:', error);
      Alert.alert('Error', 'No se pudo guardar el lote: ' + (error.message || 'Error desconocido'));
    } finally {
      setSavingLote(false);
    }
  };

  const getDiasRestantes = (fechaVencimiento) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const vence = new Date(fechaVencimiento);
    vence.setHours(0, 0, 0, 0);
    return Math.round((vence - hoy) / (1000 * 60 * 60 * 24));
  };

  const getColorDias = (dias) => {
    if (dias < 0) return '#DC3545';
    if (dias <= 7) return '#FF8C00';
    if (dias <= 30) return '#FFC107';
    return '#28A745';
  };

  if (loading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loaderText}>Cargando información del producto...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Card style={styles.card}>
        <Card.Title title="Información General" />
        <Card.Content>
          {producto.imagen && (
            <View style={styles.imageContainer}>
              <Image source={{ uri: producto.imagen }} style={styles.productImage} />
            </View>
          )}
          <Text style={styles.productName}>{producto.nombre}</Text>
          <Text style={styles.productCode}>Código: {producto.codigo}</Text>
          <Divider style={styles.divider} />
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Categoría:</Text>
            <Text style={styles.infoValue}>{producto.categoria_nombre || 'Sin categoría'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Descripción:</Text>
            <Text style={styles.infoValue}>{producto.descripcion || 'Sin descripción'}</Text>
          </View>
          
          {producto.marca && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Marca:</Text>
              <Text style={styles.infoValue}>{producto.marca}</Text>
            </View>
          )}

          {producto.codigo_barras && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Código de Barras:</Text>
              <Text style={styles.infoValue}>{producto.codigo_barras}</Text>
            </View>
          )}

          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Stock Mínimo:</Text>
            <Text style={[styles.infoValue, producto.stock_total <= (producto.stock_minimo || 0) && producto.stock_minimo > 0 && { color: '#DC3545', fontWeight: 'bold' }]}>
              {producto.stock_minimo || 0}
              {producto.stock_total <= (producto.stock_minimo || 0) && producto.stock_minimo > 0 && ' (BAJO STOCK)'}
            </Text>
          </View>
        </Card.Content>
      </Card>

      <Card style={styles.card}>
        <Card.Title title="Inventario" />
        <Card.Content>
          <View style={styles.stockContainer}>
            <View style={[
              styles.stockBadge,
              producto.stock_total < 20 ? styles.lowStock : 
              producto.stock_total >= 100 ? styles.highStock : styles.mediumStock
            ]}>
              <Text style={styles.stockValue}>{producto.stock_total || 0}</Text>
              <Text style={styles.stockLabel}>Existencias Totales</Text>
            </View>
          </View>
          
          <Divider style={styles.divider} />
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Ubicación:</Text>
            <Text style={styles.infoValue}>{producto.ubicacion}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Proveedor:</Text>
            <Text style={styles.infoValue}>{producto.proveedor}</Text>
          </View>
        </Card.Content>
      </Card>

      {producto.notas && (
        <Card style={styles.card}>
          <Card.Title title="Notas" />
          <Card.Content>
            <Text style={styles.notesText}>{producto.notas}</Text>
          </Card.Content>
        </Card>
      )}

      <Card style={styles.card}>
        <Card.Title title="Precios" />
        <Card.Content>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Precio de Venta:</Text>
            <Text style={styles.infoValue}>L. {parseFloat(producto.precio_venta || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Costo:</Text>
            <Text style={styles.infoValue}>L. {parseFloat(producto.precio_compra || 0).toFixed(2)}</Text>
          </View>
        </Card.Content>
      </Card>

      <Card style={styles.card}>
        <Card.Title
          title="Lotes del Producto"
          right={() => (
            <Button
              mode="contained"
              compact
              icon="plus"
              onPress={() => setShowLoteModal(true)}
              style={{ marginRight: 8 }}
            >
              Agregar Lote
            </Button>
          )}
        />
        <Card.Content>
          {loadingLotes ? (
            <ActivityIndicator size="small" color="#0066cc" />
          ) : lotes.filter(l => l.estado !== 'agotado' && l.estado !== 'cancelado').length === 0 ? (
            <Text style={{ color: '#888', fontStyle: 'italic' }}>No hay lotes registrados para este producto.</Text>
          ) : (
            lotes.filter(l => l.estado !== 'agotado' && l.estado !== 'cancelado').map((lote, index) => {
              const dias = getDiasRestantes(lote.fecha_vencimiento);
              const colorDias = getColorDias(dias);
              return (
                <View key={lote.id || index} style={styles.loteRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.loteNumero}>Lote: {lote.numero_lote}</Text>
                    <Text style={styles.loteFecha}>Vence: {lote.fecha_vencimiento ? new Date(lote.fecha_vencimiento).toLocaleDateString() : 'N/A'}</Text>
                    <Text style={styles.loteCantidad}>Cantidad: {lote.cantidad_actual}</Text>
                  </View>
                  <View style={[styles.lotesBadge, { backgroundColor: colorDias + '22', borderColor: colorDias }]}>
                    <Text style={[styles.lotesDiasTxt, { color: colorDias }]}>
                      {dias < 0 ? `Vencido hace ${Math.abs(dias)}d` : dias === 0 ? 'Vence hoy' : `${dias}d`}
                    </Text>
                  </View>
                </View>
              );
            })
          )}
        </Card.Content>
      </Card>

      <Modal
        visible={showLoteModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowLoteModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.loteModalContent}>
            <Text style={styles.loteModalTitle}>Agregar Lote</Text>
            <Divider style={{ marginBottom: 12 }} />

            <Text style={styles.loteFieldLabel}>Número de Lote *</Text>
            <TextInput
              style={styles.loteInput}
              value={loteForm.numero_lote}
              onChangeText={v => setLoteForm(f => ({ ...f, numero_lote: v }))}
              placeholder="Ej: LOT-2025-001"
            />

            <Text style={styles.loteFieldLabel}>Fecha de Vencimiento *</Text>
            <TouchableOpacity
              style={styles.loteDateBtn}
              onPress={() => setShowDatePickerVenc(true)}
              activeOpacity={0.7}
            >
              <Ionicons name="calendar-outline" size={18} color="#0066cc" />
              <Text style={[styles.loteDateText, !loteForm.fecha_vencimiento && styles.loteDatePlaceholder]}>
                {loteForm.fecha_vencimiento || 'Seleccionar fecha (YYYY-MM-DD)'}
              </Text>
              {loteForm.fecha_vencimiento ? (
                <TouchableOpacity
                  onPress={() => { setLoteForm(f => ({ ...f, fecha_vencimiento: '' })); setFechaVencDate(null); }}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Ionicons name="close-circle" size={18} color="#DC3545" />
                </TouchableOpacity>
              ) : (
                <Ionicons name="chevron-down" size={16} color="#888" />
              )}
            </TouchableOpacity>
            {showDatePickerVenc && (
              <DateTimePicker
                value={fechaVencDate || new Date()}
                mode="date"
                display={Platform.OS === 'ios' ? 'inline' : 'default'}
                minimumDate={new Date()}
                onChange={(event, selectedDate) => {
                  setShowDatePickerVenc(Platform.OS === 'ios');
                  if (selectedDate) {
                    setFechaVencDate(selectedDate);
                    setLoteForm(f => ({ ...f, fecha_vencimiento: formatDateYMD(selectedDate) }));
                  }
                }}
              />
            )}

            <Text style={styles.loteFieldLabel}>Cantidad Inicial *</Text>
            <TextInput
              style={styles.loteInput}
              value={loteForm.cantidad_inicial}
              onChangeText={v => setLoteForm(f => ({ ...f, cantidad_inicial: v }))}
              placeholder="0"
              keyboardType="numeric"
            />

            <Text style={styles.loteFieldLabel}>Fecha de Fabricación (opcional)</Text>
            <TouchableOpacity
              style={styles.loteDateBtn}
              onPress={() => setShowDatePickerFab(true)}
              activeOpacity={0.7}
            >
              <Ionicons name="calendar-outline" size={18} color="#0066cc" />
              <Text style={[styles.loteDateText, !loteForm.fecha_fabricacion && styles.loteDatePlaceholder]}>
                {loteForm.fecha_fabricacion || 'Seleccionar fecha (YYYY-MM-DD)'}
              </Text>
              {loteForm.fecha_fabricacion ? (
                <TouchableOpacity
                  onPress={() => { setLoteForm(f => ({ ...f, fecha_fabricacion: '' })); setFechaFabDate(null); }}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Ionicons name="close-circle" size={18} color="#DC3545" />
                </TouchableOpacity>
              ) : (
                <Ionicons name="chevron-down" size={16} color="#888" />
              )}
            </TouchableOpacity>
            {showDatePickerFab && (
              <DateTimePicker
                value={fechaFabDate || new Date()}
                mode="date"
                display={Platform.OS === 'ios' ? 'inline' : 'default'}
                maximumDate={new Date()}
                onChange={(event, selectedDate) => {
                  setShowDatePickerFab(Platform.OS === 'ios');
                  if (selectedDate) {
                    setFechaFabDate(selectedDate);
                    setLoteForm(f => ({ ...f, fecha_fabricacion: formatDateYMD(selectedDate) }));
                  }
                }}
              />
            )}

            <Text style={styles.loteFieldLabel}>Costo Unitario (opcional)</Text>
            <TextInput
              style={styles.loteInput}
              value={loteForm.costo_unitario}
              onChangeText={v => setLoteForm(f => ({ ...f, costo_unitario: v }))}
              placeholder="0.00"
              keyboardType="numeric"
            />

            <View style={styles.loteModalActions}>
              <TouchableOpacity
                style={[styles.loteActionBtn, styles.loteCancelBtn]}
                onPress={() => {
                  setShowLoteModal(false);
                  setLoteForm({ numero_lote: '', fecha_vencimiento: '', cantidad_inicial: '', fecha_fabricacion: '', costo_unitario: '' });
                  setFechaVencDate(null);
                  setFechaFabDate(null);
                }}
                disabled={savingLote}
              >
                <Text style={styles.loteCancelTxt}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.loteActionBtn, styles.loteSaveBtn, savingLote && { opacity: 0.6 }]}
                onPress={handleGuardarLote}
                disabled={savingLote}
              >
                <Text style={styles.loteSaveTxt}>{savingLote ? 'Guardando...' : 'Guardar'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Card style={styles.card}>
        <Card.Title title="Información Adicional" />
        <Card.Content>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Fecha de creación:</Text>
            <Text style={styles.infoValue}>{producto.created_at ? new Date(producto.created_at).toLocaleDateString() : 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Última actualización:</Text>
            <Text style={styles.infoValue}>{producto.updated_at ? new Date(producto.updated_at).toLocaleDateString() : 'N/A'}</Text>
          </View>
        </Card.Content>
      </Card>

      {user?.rol !== 'vendedor' && (
        <View style={styles.buttonContainer}>
          <Button
            mode="contained"
            icon="pencil"
            onPress={handleEdit}
            style={styles.editButton}
          >
            Editar Producto
          </Button>

          <Button
            mode="outlined"
            icon="delete"
            onPress={handleDelete}
            style={styles.deleteButton}
            textColor="red"
          >
            Eliminar
          </Button>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 10,
  },
  card: {
    marginBottom: 15,
    elevation: 2,
  },
  productName: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  productCode: {
    fontSize: 14,
    color: '#666',
    marginBottom: 10,
  },
  divider: {
    marginVertical: 10,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 5,
  },
  infoLabel: {
    fontWeight: 'bold',
    color: '#555',
  },
  infoValue: {
    color: '#333',
  },
  stockContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  stockBadge: {
    flex: 1,
    alignItems: 'center',
    padding: 10,
    margin: 5,
    borderRadius: 5,
    backgroundColor: '#e0e0e0',
  },
  lowStock: {
    backgroundColor: '#ffcccc',
  },
  mediumStock: {
    backgroundColor: '#ffffcc',
  },
  highStock: {
    backgroundColor: '#ccffcc',
  },
  stockValue: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  stockLabel: {
    fontSize: 12,
    color: '#555',
  },
  notesText: {
    fontStyle: 'italic',
    marginTop: 5,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 20,
    paddingHorizontal: 10,
  },
  editButton: {
    flex: 1,
    marginRight: 10,
  },
  deleteButton: {
    flex: 1,
    borderColor: 'red',
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loaderText: {
    marginTop: 10,
  },
  imageContainer: {
    alignItems: 'center',
    marginBottom: 15,
  },
  productImage: {
    width: 150,
    height: 150,
    borderRadius: 8,
    resizeMode: 'cover',
  },
  loteRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  loteNumero: {
    fontWeight: 'bold',
    fontSize: 14,
    color: '#333',
  },
  loteFecha: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  loteCantidad: {
    fontSize: 12,
    color: '#555',
    marginTop: 2,
  },
  lotesBadge: {
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderWidth: 1,
    marginLeft: 8,
  },
  lotesDiasTxt: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loteModalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 480,
  },
  loteModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  loteFieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#555',
    marginBottom: 4,
    marginTop: 10,
  },
  loteInput: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#fafafa',
  },
  loteDateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 10,
    backgroundColor: '#fafafa',
    gap: 8,
  },
  loteDateText: {
    flex: 1,
    fontSize: 14,
    color: '#333',
  },
  loteDatePlaceholder: {
    color: '#999',
  },
  loteModalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
    gap: 10,
  },
  loteActionBtn: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  loteCancelBtn: {
    backgroundColor: '#f0f0f0',
  },
  loteCancelTxt: {
    color: '#555',
    fontWeight: '600',
  },
  loteSaveBtn: {
    backgroundColor: '#0066cc',
  },
  loteSaveTxt: {
    color: 'white',
    fontWeight: '600',
  },
});

export default ProductoDetalleScreen;
